import {useState} from 'react';
import {type Editor} from '@tiptap/react';
import {
  Box,
  IconButton,
  Tooltip,
  styled,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import CodeIcon from '@mui/icons-material/Code';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';

// ----------------------------------------------------------------------

const StyledToolbar = styled(Box)(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 2,
  padding: theme.spacing(0.5, 1),
  borderBottom: `solid 1px ${theme.palette.divider}`,
  direction: 'ltr',
  '& .MuiIconButton-root': {
    borderRadius: 4,
    padding: 4,
    width: 28,
    height: 28,
  },
}));

const ToolbarDivider = () => (
  <Divider orientation="vertical" flexItem sx={{mx: 0.5, my: 0.5}} />
);

type EditorToolbarProps = {
  editor: Editor;
  simple?: boolean;
  imagesVideoOpen?: boolean;
};

export default function Toolbar({editor, simple, imagesVideoOpen}: EditorToolbarProps) {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const handleOpenLinkDialog = () => {
    const previousUrl = editor.getAttributes('link').href || '';
    setLinkUrl(previousUrl || 'https://');
    setShowLinkDialog(true);
  };

  const handleInsertLink = () => {
    if (linkUrl === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({href: linkUrl}).run();
    }
    setLinkUrl('');
    setShowLinkDialog(false);
  };

  const handleCloseLinkDialog = () => {
    setLinkUrl('');
    setShowLinkDialog(false);
  };

  const handleOpenImageDialog = () => {
    setImageUrl('');
    setShowImageDialog(true);
  };

  const handleInsertImage = () => {
    if (imageUrl) {
      editor.chain().focus().setImage({src: imageUrl}).run();
    }
    setImageUrl('');
    setShowImageDialog(false);
  };

  const handleCloseImageDialog = () => {
    setImageUrl('');
    setShowImageDialog(false);
  };

  const isShowImages = imagesVideoOpen === undefined || imagesVideoOpen === true;

  return (
    <>
      <StyledToolbar>
        {/* Headings */}
        <select
          value={
            editor.isActive('heading', {level: 1}) ? '1' :
            editor.isActive('heading', {level: 2}) ? '2' :
            editor.isActive('heading', {level: 3}) ? '3' :
            editor.isActive('heading', {level: 4}) ? '4' :
            editor.isActive('heading', {level: 5}) ? '5' :
            editor.isActive('heading', {level: 6}) ? '6' : ''
          }
          onChange={(e) => {
            const val = e.target.value;
            if (val === '') {
              editor.chain().focus().setParagraph().run();
            } else {
              const level = parseInt(val, 10) as 1 | 2 | 3 | 4 | 5 | 6;
              editor.chain().focus().setHeading({level}).run();
            }
          }}
          style={{
            height: 28,
            border: '1px solid #ddd',
            borderRadius: 4,
            fontSize: 13,
            padding: '0 4px',
            cursor: 'pointer',
            background: 'transparent',
          }}
        >
          <option value="">Normal</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
          <option value="4">Heading 4</option>
          <option value="5">Heading 5</option>
          <option value="6">Heading 6</option>
        </select>

        <ToolbarDivider />

        {/* Text formatting */}
        <Tooltip title="Bold">
          <IconButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            color={editor.isActive('bold') ? 'primary' : 'default'}
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            color={editor.isActive('italic') ? 'primary' : 'default'}
          >
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Underline">
          <IconButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            color={editor.isActive('underline') ? 'primary' : 'default'}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Strikethrough">
          <IconButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            color={editor.isActive('strike') ? 'primary' : 'default'}
          >
            <StrikethroughSIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <ToolbarDivider />

        {/* Lists */}
        <Tooltip title="Ordered List">
          <IconButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            color={editor.isActive('orderedList') ? 'primary' : 'default'}
          >
            <FormatListNumberedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Bullet List">
          <IconButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            color={editor.isActive('bulletList') ? 'primary' : 'default'}
          >
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <ToolbarDivider />

        {/* Alignment */}
        <Tooltip title="Align Left">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            color={editor.isActive({textAlign: 'left'}) ? 'primary' : 'default'}
          >
            <FormatAlignLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Align Center">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            color={editor.isActive({textAlign: 'center'}) ? 'primary' : 'default'}
          >
            <FormatAlignCenterIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Align Right">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            color={editor.isActive({textAlign: 'right'}) ? 'primary' : 'default'}
          >
            <FormatAlignRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {!simple && (
          <>
            <ToolbarDivider />

            {/* Block elements */}
            <Tooltip title="Code Block">
              <IconButton
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                color={editor.isActive('codeBlock') ? 'primary' : 'default'}
              >
                <CodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Blockquote">
              <IconButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                color={editor.isActive('blockquote') ? 'primary' : 'default'}
              >
                <FormatQuoteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Horizontal Rule">
              <IconButton
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
              >
                <HorizontalRuleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}

        <ToolbarDivider />

        {/* Insert */}
        <Tooltip title="Link">
          <IconButton
            onClick={handleOpenLinkDialog}
            color={editor.isActive('link') ? 'primary' : 'default'}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {isShowImages && (
          <Tooltip title="Image">
            <IconButton onClick={handleOpenImageDialog}>
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <ToolbarDivider />

        {/* Clear formatting */}
        <Tooltip title="Clear Formatting">
          <IconButton
            onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          >
            <FormatClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </StyledToolbar>

      {/* Link Dialog */}
      <Dialog
        open={showLinkDialog}
        onClose={handleCloseLinkDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="URL"
            placeholder="https://"
            fullWidth
            variant="outlined"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleInsertLink();
              }
            }}
            sx={{mt: 1}}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkDialog}>Cancel</Button>
          <Button onClick={handleInsertLink} variant="contained">
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image URL Dialog */}
      <Dialog
        open={showImageDialog}
        onClose={handleCloseImageDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Insert Image URL</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Image URL"
            placeholder="https://"
            fullWidth
            variant="outlined"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleInsertImage();
              }
            }}
            sx={{mt: 1}}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageDialog}>Cancel</Button>
          <Button onClick={handleInsertImage} variant="contained">
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
